import { ReactComponent as DebrisDumpSvg } from 'images/icons/debris_dump.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as RefillingSvg } from 'images/icons/refilling.svg'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { ReactComponent as WasteWaterDrainSvg } from 'images/icons/waste_water_drain.svg'
import {
  ACTION_CHARGING,
  ACTION_DEBRIS_DUMP,
  ACTION_PARKING,
  ACTION_REFILLING,
  ACTION_WASHING,
  ACTION_WASTE_WATER_DRAIN,
} from 'pages/Robot/RobotView/RobotLeftBlock/Service/serviceCalibUtils'
import type { FC, SVGProps } from 'react'
import { ROBOT_MODES } from 'utils/constants'
import type { IModeSwitchPending } from 'utils/interfaces'

import {
  type CatalogOperationRobotInfo,
  isCatalogOperationVisible,
  selectCatalogOperation,
} from './selectCatalogOperation'

export type ServicePointActivityKind = 'service' | 'calibrate' | 'reset'

export type ServicePointModePresentation = {
  progressLabel: string
  iconTitle: string
  Icon: FC<SVGProps<SVGSVGElement>>
  activity: ServicePointActivityKind
}

const CATALOG_POINT_MODES = new Set<number>([
  ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT,
  ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT,
  ROBOT_MODES.MODE_SERVICE_POINT,
])

const ICON_BY_ACTION: Record<number, FC<SVGProps<SVGSVGElement>>> = {
  [ACTION_PARKING]: ParkingSvg,
  [ACTION_WASHING]: WashingSvg,
  [ACTION_CHARGING]: ChargingSvg,
  [ACTION_REFILLING]: RefillingSvg,
  [ACTION_WASTE_WATER_DRAIN]: WasteWaterDrainSvg,
  [ACTION_DEBRIS_DUMP]: DebrisDumpSvg,
}

const ICON_TITLE_BY_ACTION: Record<number, string> = {
  [ACTION_PARKING]: 'Parking',
  [ACTION_WASHING]: 'Washing',
  [ACTION_CHARGING]: 'Charging',
  [ACTION_REFILLING]: 'Refilling',
  [ACTION_WASTE_WATER_DRAIN]: 'Draining',
  [ACTION_DEBRIS_DUMP]: 'Discharge',
}

const SLOT_LABEL_BY_ACTION: Record<number, string> = {
  [ACTION_PARKING]: 'Parking slot',
  [ACTION_WASHING]: 'Washing slot',
  [ACTION_CHARGING]: 'Charging slot',
  [ACTION_REFILLING]: 'Refilling slot',
  [ACTION_DEBRIS_DUMP]: 'Discharge slot',
  [ACTION_WASTE_WATER_DRAIN]: 'Drain point',
}

const SERVICE_PROGRESS_BY_ACTION: Record<number, string> = {
  [ACTION_PARKING]: 'Parking is in progress',
  [ACTION_WASHING]: 'Washing is in progress',
  [ACTION_CHARGING]: 'Going to the charging station',
  [ACTION_REFILLING]: 'Refilling is in progress',
  [ACTION_WASTE_WATER_DRAIN]: 'Draining is in progress',
  [ACTION_DEBRIS_DUMP]: 'Discharge is in progress',
}

const FALLBACK_SERVICE: ServicePointModePresentation = {
  progressLabel: 'Service point action in progress',
  iconTitle: 'Service point action',
  Icon: WashingSvg,
  activity: 'service',
}

const FALLBACK_CALIBRATE: ServicePointModePresentation = {
  progressLabel: 'Service point is calibrating',
  iconTitle: 'Calibration',
  Icon: WashingSvg,
  activity: 'calibrate',
}

const FALLBACK_RESET: ServicePointModePresentation = {
  progressLabel: 'Service point calibration is resetting',
  iconTitle: 'Resetting calibration',
  Icon: WashingSvg,
  activity: 'reset',
}

type ParsedServicePointRef = {
  kind?: number
  action_type?: number[]
  lot_model?: string
  maintenance_point_id?: number
}

function resolveCatalogActivity(
  activeMode: ROBOT_MODES | null | undefined,
  pendingMode: ROBOT_MODES | null | undefined
): ServicePointActivityKind | null {
  const modesToCheck = [activeMode, pendingMode].filter((mode): mode is ROBOT_MODES => mode != null)
  for (const mode of modesToCheck) {
    if (mode === ROBOT_MODES.MODE_SERVICE_POINT) {
      return 'service'
    }
    if (mode === ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT) {
      return 'calibrate'
    }
    if (mode === ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT) {
      return 'reset'
    }
  }
  return null
}

function isCatalogPointModeActive(
  activeMode: ROBOT_MODES | null | undefined,
  pendingMode: ROBOT_MODES | null | undefined
): boolean {
  return [activeMode, pendingMode].some(mode => mode != null && CATALOG_POINT_MODES.has(mode))
}

function resolveActionTypeFromActiveMetadata(
  metadata: ParsedServicePointRef | null | undefined,
  activity: ServicePointActivityKind | null
): number | null {
  if (!metadata || !activity) {
    return null
  }

  // Service (mode 17): action_type is part of the command wire format, not lot_model.
  if (activity === 'service') {
    const serviceCode = metadata.action_type?.[0]
    if (serviceCode != null && Number.isFinite(Number(serviceCode))) {
      return Number(serviceCode)
    }
  }

  return null
}

export function resolveServicePointActionType(
  activeMode: ROBOT_MODES | null | undefined,
  options?: {
    activeModeMetadata?: ParsedServicePointRef | null
    modeSwitchPending?: IModeSwitchPending | null
    catalogServiceActionType?: number | null
  }
): number | null {
  const pendingMode = options?.modeSwitchPending?.mode
  if (!isCatalogPointModeActive(activeMode, pendingMode)) {
    return null
  }

  if (options?.catalogServiceActionType != null) {
    return options.catalogServiceActionType
  }

  const activity = resolveCatalogActivity(activeMode, pendingMode)
  return resolveActionTypeFromActiveMetadata(options?.activeModeMetadata, activity)
}

function buildPresentation(
  activity: ServicePointActivityKind,
  actionType: number | null | undefined
): ServicePointModePresentation {
  const Icon = (actionType != null ? ICON_BY_ACTION[actionType] : undefined) ?? WashingSvg
  const iconTitle =
    (actionType != null ? ICON_TITLE_BY_ACTION[actionType] : undefined) ??
    (activity === 'service'
      ? 'Service point action'
      : activity === 'calibrate'
      ? 'Calibration'
      : 'Resetting calibration')

  if (activity === 'service') {
    if (actionType == null) {
      return FALLBACK_SERVICE
    }
    return {
      progressLabel: SERVICE_PROGRESS_BY_ACTION[actionType] ?? FALLBACK_SERVICE.progressLabel,
      iconTitle,
      Icon,
      activity,
    }
  }

  const slotLabel = actionType != null ? SLOT_LABEL_BY_ACTION[actionType] : null
  if (activity === 'calibrate') {
    return {
      progressLabel: slotLabel ? `${slotLabel} is calibrating` : FALLBACK_CALIBRATE.progressLabel,
      iconTitle,
      Icon,
      activity,
    }
  }

  return {
    progressLabel: slotLabel ? `${slotLabel} calibration is resetting` : FALLBACK_RESET.progressLabel,
    iconTitle,
    Icon,
    activity,
  }
}

export function resolveServicePointModePresentation(
  activeMode: ROBOT_MODES | null | undefined,
  options?: {
    activeModeMetadata?: ParsedServicePointRef | null
    modeSwitchPending?: IModeSwitchPending | null
    catalogServiceActionType?: number | null
  }
): ServicePointModePresentation | null {
  const pendingMode = options?.modeSwitchPending?.mode
  const activity = resolveCatalogActivity(activeMode, pendingMode)
  if (activity == null) {
    return null
  }
  const actionType = resolveServicePointActionType(activeMode, options)
  return buildPresentation(activity, actionType)
}

/** Unified Status / list / catalog presentation from server-derived catalog operation (MR-04n). */
export function resolveCatalogOperationPresentation(
  robotInfo: CatalogOperationRobotInfo | null | undefined
): ServicePointModePresentation | null {
  const operation = selectCatalogOperation(robotInfo)
  if (!isCatalogOperationVisible(operation)) {
    return null
  }

  return resolveServicePointModePresentation(operation.effectiveMode, {
    catalogServiceActionType: operation.actionType,
  })
}

/** @deprecated Use resolveServicePointModePresentation */
export function resolveCatalogServicePresentation(
  activeMode: ROBOT_MODES | null | undefined,
  options?: {
    activeModeMetadata?: ParsedServicePointRef | null
    modeSwitchPending?: IModeSwitchPending | null
    catalogServiceActionType?: number | null
  }
): ServicePointModePresentation | null {
  const presentation = resolveServicePointModePresentation(activeMode, options)
  if (!presentation || presentation.activity !== 'service') {
    return null
  }
  return presentation
}
